import { FC, useEffect, useState } from "react";
import { Control, FieldValues, UseFormWatch } from "react-hook-form";
import ModalComponent from "../../../components/organism/Modal/ModalComponent";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import { Button, Flex, HStack } from "@chakra-ui/react";
import api from "../../../utils/api";
import { AUDIT_DOWNLOAD_DEFAULT_URL } from "../../../utils/endpoints";
import { Dayjs } from "dayjs";
import { Option } from "../../../types/General/Option";
import { getAuditTypologiesNonAdmin } from "../../../services/Audit/getAuditTypologiesNonAdmin";
import FormSelect from "../../../components/molecule/Select/FormSelect";
import DatePickerComponent from "../../../components/molecule/DatePicker/DatePickerComponent";
import FileDownload from "../../../components/molecule/FileDownload/FileDownload";

interface ModalDownloadAuditProps {
  control: Control<any>;
  errors: any;
  setValue: any;
  modalConfig: ModalConfig;
  watch: UseFormWatch<FieldValues>;
}

const ModalDownloadAudit: FC<ModalDownloadAuditProps> = ({
  control,
  errors,
  setValue,
  modalConfig,
  watch,
}) => {
  const { isOpen, title = "", buttonTitle, closeModal } = modalConfig;
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedUntilDate, setSelectedUntilDate] = useState<Dayjs | null>(
    null,
  );
  const [downloadFile, setDownloadFile] = useState<File | null>(null);
  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValue("typeOption", null);
      setValue("FromDate", null);
      setValue("UntilDate", null);
      setSelectedFromDate(null);
      setSelectedUntilDate(null);
    }
  }, [isOpen, setValue]);

  useEffect(() => {
    const prepareFile = async () => {
      const typologyName = (watch("typeOption") as Option | undefined)?.name;
      try {
        setIsPreparing(true);
        const params: Record<string, string | undefined> = {
          Typology: typologyName,
          FromDate: selectedFromDate
            ? selectedFromDate.format("M-D-YYYY")
            : undefined,
          UntilDate: selectedUntilDate
            ? selectedUntilDate.format("M-D-YYYY")
            : undefined,
        };
        Object.keys(params).forEach(
          (k) => params[k] === undefined && delete params[k],
        );
        const response = await api.get(AUDIT_DOWNLOAD_DEFAULT_URL, {
          params,
          responseType: "blob",
        });
        const blob: Blob = response.data;
        const now = new Date();
        const timestamp = now.toISOString().replace(/\D/g, "").slice(0, 14);
        const fileName = `Auditoria_${timestamp}.xlsx`;
        const file = new File([blob], fileName, {
          type: blob.type || "application/octet-stream",
        });
        setDownloadFile(file);
      } catch (error) {
        console.log("Prepare file error:", error);
        setDownloadFile(null);
      } finally {
        setIsPreparing(false);
      }
    };
    prepareFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("typeOption"), selectedFromDate, selectedUntilDate]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={closeModal}
      title={title}
      isAlertOpen={isAlertOpen}
      setIsAlertOpen={setIsAlertOpen}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Flex gap="4" direction="column">
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="typeOption"
            fieldLabel="Tipo"
            fetchOptions={getAuditTypologiesNonAdmin}
            isRequiredCondition={() => false}
          />
          <Flex gap="4" alignItems="flex-end">
            <DatePickerComponent
              label="Período da auditoria:"
              fieldName="FromDate"
              selectedDate={selectedFromDate}
              onChange={(date: Dayjs | null) => {
                setSelectedFromDate(date);
              }}
              mode="date"
              format="DD/MM/YYYY"
              control={control}
            />
            a
            <DatePickerComponent
              label={"\u00A0"}
              fieldName="UntilDate"
              selectedDate={selectedUntilDate}
              onChange={(date: Dayjs | null) => {
                setSelectedUntilDate(date);
              }}
              mode="date"
              format="DD/MM/YYYY"
              control={control}
              minDate={selectedFromDate || undefined}
            />
          </Flex>
        </Flex>
        <HStack justifyContent="flex-end" pt="8" pb="4">
          <Button
            variant="tertiary"
            onClick={() => closeModal()}
            isDisabled={isPreparing}
          >
            Cancelar
          </Button>
          <FileDownload file={downloadFile} buttonName={buttonTitle} />
        </HStack>
      </form>
    </ModalComponent>
  );
};

export default ModalDownloadAudit;
